import type {
  ConnectionApp,
  ConnectionScope,
  ConnectionStatus,
  ConnectionSummary,
  Decision,
} from "@typedoc/contracts/connections";
import type { ReactNode } from "react";
import type { WebsiteLogin, WebsiteLoginStatus } from "@typedoc/contracts/connections";

/** Joins the classes that apply. The web app has no class merger, so callers never pass conflicts. */
export const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(" ");

export type Tone = "success" | "warning" | "muted" | "danger";

/** One word or two per state, in the words a person would use about an account. */
export const STATUS_COPY: Record<ConnectionStatus, { label: string; tone: Tone }> = {
  connected: { label: "Connected", tone: "success" },
  connecting: { label: "Connecting", tone: "muted" },
  needs_renewal: { label: "Needs renewal", tone: "warning" },
  paused: { label: "Paused", tone: "muted" },
  revoked: { label: "Access removed", tone: "danger" },
  disconnected: { label: "Disconnected", tone: "muted" },
};

/** A website login's state. "Not tried yet" is honest about a password nobody has used. */
export const WEBSITE_STATUS_COPY: Record<WebsiteLoginStatus, { label: string; tone: Tone }> = {
  not_tried: { label: "Not tried yet", tone: "muted" },
  connected: { label: "Connected", tone: "success" },
  password_rejected: { label: "Password rejected", tone: "warning" },
};

/** The two states nobody can fix by waiting: someone has to sign in again. */
export const needsAttention = (status: ConnectionStatus) =>
  status === "needs_renewal" || status === "revoked";

export const scopeLabel = (scope: ConnectionScope) =>
  scope === "workspace" ? "Workspace" : "Personal";

export const DECISION_OPTIONS: readonly {
  value: Decision;
  label: string;
  tone: "default" | "success" | "danger";
}[] = [
  { value: "always", label: "Always", tone: "success" },
  { value: "ask_first", label: "Ask first", tone: "default" },
  { value: "never", label: "Never", tone: "danger" },
];

export const DECISION_LABEL: Record<Decision, string> = {
  always: "Always",
  ask_first: "Ask first",
  never: "Never",
};

export type ListFilter = "all" | "attention";

/**
 * A status, the owners chosen and a search term. `owned` says whether the Owner menu keeps an
 * item; `ownerOf` is the Owner column's text, when the list has one, so a name a row shows can be
 * searched for.
 */
type ListQuery<T> = {
  filter: ListFilter;
  owned?: ((item: T) => boolean) | undefined;
  term: string;
  ownerOf?: ((item: T) => string) | undefined;
};

/** What the list shows for a status, the owners and a search term, matched on what a row displays. */
export function filterConnections(
  connections: readonly ConnectionSummary[],
  { filter, owned, term, ownerOf }: ListQuery<ConnectionSummary>,
) {
  const needle = term.trim().toLowerCase();
  return connections.filter(
    (connection) =>
      (filter === "all" || needsAttention(connection.status)) &&
      (owned?.(connection) ?? true) &&
      (!needle ||
        [
          connection.app.name,
          connection.label,
          connection.providerIdentity ?? "",
          ownerOf?.(connection) ?? "",
        ].some((text) => text.toLowerCase().includes(needle))),
  );
}

/**
 * What the list area shows. A failed load shows nothing there, only the notice above it: "No
 * accounts yet" would claim something the page does not know.
 */
export function listView({
  pending,
  failed,
  total,
  shown,
}: {
  pending: boolean;
  failed: boolean;
  total: number;
  shown: number;
}): "failed" | "loading" | "empty" | "no-match" | "rows" {
  if (failed) return "failed";
  if (pending) return "loading";
  if (total === 0) return "empty";
  return shown === 0 ? "no-match" : "rows";
}

/**
 * What a website login shows for a status, the owners and a search term — the same rules as
 * `filterConnections`, matched on the name, the address and the username.
 */
export function filterWebsiteLogins(
  logins: readonly WebsiteLogin[],
  { filter, owned, term, ownerOf }: ListQuery<WebsiteLogin>,
) {
  const needle = term.trim().toLowerCase();
  return logins.filter(
    (login) =>
      (filter === "all" || login.status === "password_rejected") &&
      (owned?.(login) ?? true) &&
      (!needle ||
        [login.label, login.host, login.username, "website login", ownerOf?.(login) ?? ""].some(
          (text) => text.toLowerCase().includes(needle),
        )),
  );
}

/** A website login on the same tile as an app: the site's icon once fetched, else its initial. */
export const websiteMark = (login: Pick<WebsiteLogin, "host" | "label" | "iconUrl">) => ({
  slug: login.host,
  name: login.label,
  logo: login.iconUrl,
});

export function StatusText({
  status,
  className,
}: {
  status: ConnectionStatus;
  className?: string | undefined;
}) {
  return <StatusLabel copy={STATUS_COPY[status]} className={className} />;
}

/** A status as a dot and a word, for anything that has one. */
export function StatusLabel({
  copy,
  className,
}: {
  copy: { label: string; tone: Tone };
  className?: string | undefined;
}) {
  return (
    <span className={cx("inline-flex items-center gap-1.5 text-[13px]/5", className)}>
      <span
        aria-hidden="true"
        className={cx(
          "size-1.5 shrink-0 rounded-full",
          copy.tone === "success" && "bg-success",
          copy.tone === "warning" && "bg-warning",
          copy.tone === "danger" && "bg-destructive",
          copy.tone === "muted" && "bg-muted-foreground",
        )}
      />
      {copy.label}
    </span>
  );
}

/** The app's own logo when Composio has one, else its initial on the same tile. */
export function AppMark({ app, size = 36 }: { app: ConnectionApp; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      className="grid shrink-0 place-items-center overflow-hidden rounded-[22%] border border-border bg-card font-medium text-muted-foreground"
    >
      {app.logo ? (
        <img src={app.logo} alt="" className="size-[64%] object-contain" />
      ) : (
        app.name.slice(0, 1).toUpperCase()
      )}
    </span>
  );
}

/** A mono eyebrow, the register the boards use for section labels inside a card. */
export function Eyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <p
      className={cx(
        "m-0 font-mono text-[10px]/3 tracking-[0.08em] text-subtle-foreground uppercase",
        className,
      )}
    >
      {children}
    </p>
  );
}

/** A direct connection's mark: the app plugged straight in, with no browser in between. */
export function PlugGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 2.5v3M10 2.5v3M4.5 5.5h7v2.5a3.5 3.5 0 0 1-7 0V5.5zM8 11.5v2" />
    </svg>
  );
}

/** A website login's mark: a browser window, since a teammate signs in the way a person does. */
export function BrowserGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="3" width="12" height="10" rx="2" />
      <path d="M2 6.5h12" />
    </svg>
  );
}

/**
 * Menu glyphs: an action in a menu is an icon and a word, never a word trailing off into "…".
 * 14px, stroked in the item's own colour, so a destructive item's icon is red with its label.
 */
function MenuGlyph({ d }: { d: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

export const RemoveGlyph = () => (
  <MenuGlyph d="M2.5 4.5h11M6.5 4.5V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5M4 4.5l.7 8.6a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9l.7-8.6M6.75 7v4.5M9.25 7v4.5" />
);

/** A plug pulled out of its socket, a gap between them: the connection, unplugged. */
export const DisconnectGlyph = () => (
  <MenuGlyph d="M7 5H4.5a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2H7zM7 6.75h1.75M7 9.25h1.75M11.25 5h1.25a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-1.25zM1 8h1.5" />
);

export const RenewGlyph = () => <MenuGlyph d="M13 8a5 5 0 1 1-1.5-3.55M13 2.5V5h-2.5" />;

/** "Oct 10, 3:42 PM" — US order, no seconds, the year only when it isn't this one. */
export function formatWhen(iso: string, now = new Date()) {
  const date = new Date(iso);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  });
}

const RULE_WORDS: Record<string, string> = {
  "read-only-hint": "Only reads",
  send: "Sends messages",
  money: "Moves or records money",
  settings: "Changes settings or sharing",
  delete: "Deletes or changes in bulk",
  "directory-wide": "Reaches other people's data",
  "write-fallback": "Changes records",
};

/**
 * Why an action sits in its category, in words. The server records the rule or exception that
 * placed it ("rule: send", "exception: drafts never leave the mailbox", "argument: …"); an
 * exception already carries its reason, and an unknown rule falls back to the raw text rather
 * than to nothing.
 */
export function reasonLabel(reason: string) {
  const [kind, ...rest] = reason.split(":");
  const detail = rest.join(":").trim();
  if (kind === "exception" && detail) return detail;
  if (kind === "argument") return RULE_WORDS["directory-wide"];
  if (kind === "rule" && RULE_WORDS[detail]) return RULE_WORDS[detail];
  return reason;
}
